// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const DEMO_REFERRALS = [
  { id: 1, name: 'Sarah Chen', email: 's.chen@example.com', status: 'Converted', date: 'Feb 15, 2026', plan: 'Growth', commission: '$49.90', paidOut: true },
  { id: 2, name: 'Marcus Johnson', email: 'm.johnson@example.com', status: 'Converted', date: 'Feb 10, 2026', plan: 'Scale', commission: '$99.90', paidOut: true },
  { id: 3, name: 'Emily Park', email: 'e.park@example.com', status: 'Trial', date: 'Feb 18, 2026', plan: '—', commission: 'Pending', paidOut: false },
  { id: 4, name: 'David Martinez', email: 'd.martinez@example.com', status: 'Signed Up', date: 'Feb 19, 2026', plan: '—', commission: '—', paidOut: false },
  { id: 5, name: 'Alex Thompson', email: 'a.thompson@example.com', status: 'Converted', date: 'Jan 28, 2026', plan: 'Growth', commission: '$49.90', paidOut: false },
  { id: 6, name: 'Priya Sharma', email: 'p.sharma@example.com', status: 'Expired', date: 'Jan 5, 2026', plan: '—', commission: '—', paidOut: false },
];

const TIERS = [
  { name: 'Bronze', range: '0-4', commission: '10%', perks: 'Standard referral link, email templates', color: '#cd7f32' },
  { name: 'Silver', range: '5-14', commission: '15%', perks: '+ Custom landing page, priority support', color: '#c0c0c0' },
  { name: 'Gold', range: '15-29', commission: '20%', perks: '+ Co-branded materials, quarterly bonus', color: '#d4a843' },
  { name: 'Platinum', range: '30+', commission: '25%', perks: '+ Dedicated manager, annual retreat invite', color: '#e5e4e2' },
];

const AFFILIATE_TOOLS = [
  { name: 'Referral Link', icon: '🔗', desc: 'Share your unique link anywhere' },
  { name: 'Email Templates', icon: '📧', desc: '5 pre-written referral emails' },
  { name: 'Social Posts', icon: '📱', desc: 'Ready-to-share social media content' },
  { name: 'Banner Ads', icon: '🖼️', desc: 'Web banners in all standard sizes' },
  { name: 'Landing Page', icon: '🌐', desc: 'Custom co-branded landing page' },
  { name: 'QR Code', icon: '📷', desc: 'Scannable referral QR code' },
];

const S = {
  page: { padding: '24px', maxWidth: 1200, margin: '0 auto' },
  title: { fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: 0 },
  subtitle: { fontSize: 14, color: '#999', marginTop: 4 },
  tabs: { display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' },
  tab: (active) => ({ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: active ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: active ? '#000' : 'var(--text)' }),
  card: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid var(--border, #2a2a4a)' },
  cardTitle: { fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 12 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 },
  stat: { background: 'var(--card, #1a1a2e)', borderRadius: 12, padding: 20, textAlign: 'center', border: '1px solid var(--border, #2a2a4a)' },
  statValue: { fontSize: 28, fontWeight: 800, color: 'var(--accent, #d4a843)' },
  statLabel: { fontSize: 12, color: '#888', marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid var(--border, #2a2a4a)', color: '#999', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--border, #2a2a4a)', color: 'var(--text)' },
  badge: (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: `${color}22`, color }),
  btn: (primary) => ({ padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: primary ? 'var(--accent, #d4a843)' : 'var(--card, #1a1a2e)', color: primary ? '#000' : 'var(--text)', border: primary ? 'none' : '1px solid var(--border, #2a2a4a)' }),
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border, #2a2a4a)', background: 'var(--bg, #0f0f23)', color: 'var(--text)', fontSize: 14, boxSizing: 'border-box' },
};

function ReferralProgramPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('dashboard');
  const [referralLink] = useState('https://theforgestandard.com/ref/YOUR-CODE');
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  const totalReferrals = DEMO_REFERRALS.length;
  const converted = DEMO_REFERRALS.filter(r => r.status === 'Converted').length;
  const totalEarnings = DEMO_REFERRALS.filter(r => r.commission && r.commission.startsWith('$')).reduce((s, r) => s + parseFloat(r.commission.replace('$', '')), 0);
  const pendingPayout = DEMO_REFERRALS.filter(r => r.commission && r.commission.startsWith('$') && !r.paidOut).reduce((s, r) => s + parseFloat(r.commission.replace('$', '')), 0);
  const conversionRate = totalReferrals > 0 ? Math.round((converted / totalReferrals) * 100) : 0;
  const currentTier = converted < 5 ? TIERS[0] : converted < 15 ? TIERS[1] : converted < 30 ? TIERS[2] : TIERS[3];

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'referrals', label: 'My Referrals', icon: '👥' },
    { id: 'tools', label: 'Marketing Tools', icon: '🛠️' },
    { id: 'payouts', label: 'Payouts', icon: '💰' },
    { id: 'tiers', label: 'Reward Tiers', icon: '🏆' },
  ];

  return (
    <div style={S.page}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={S.title}>🤝 Referral & Affiliate Program</h1>
        <p style={S.subtitle}>Earn commissions by referring new business owners to The Forge Standard</p>
      </div>

      <div style={S.tabs}>
        {TABS.map(t => (
          <button key={t.id} style={S.tab(tab === t.id)} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        <div style={S.stat}><div style={S.statValue}>{totalReferrals}</div><div style={S.statLabel}>Total Referrals</div></div>
        <div style={S.stat}><div style={S.statValue}>{converted}</div><div style={S.statLabel}>Converted</div></div>
        <div style={S.stat}><div style={S.statValue}>{conversionRate}%</div><div style={S.statLabel}>Conversion Rate</div></div>
        <div style={S.stat}><div style={S.statValue}>${totalEarnings.toFixed(2)}</div><div style={S.statLabel}>Total Earned</div></div>
        <div style={S.stat}><div style={{ ...S.statValue, color: '#4caf50' }}>${pendingPayout.toFixed(2)}</div><div style={S.statLabel}>Pending Payout</div></div>
      </div>

      {/* Dashboard Tab */}
      {tab === 'dashboard' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Your Referral Link</div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <input style={{ ...S.input, flex: 1, fontFamily: 'monospace' }} value={referralLink} readOnly />
              <button style={S.btn(true)} onClick={copyLink}>{copied ? '✅ Copied!' : '📋 Copy'}</button>
            </div>
            <div style={{ fontSize: 13, color: '#888' }}>Share this link with potential business owners. You earn {currentTier.commission} commission on every paid conversion.</div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Quick Invite</div>
            <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Send a personalized invitation email directly from here.</p>
            <div style={{ display: 'flex', gap: 12 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder="friend@business.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
              <button style={S.btn(true)}>📧 Send Invite</button>
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Current Tier: <span style={{ color: currentTier.color }}>{currentTier.name}</span></div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
              {TIERS.map((t, i) => (
                <div key={i} style={{ flex: 1, height: 8, borderRadius: 4, background: t.name === currentTier.name ? t.color : 'rgba(255,255,255,0.1)' }} />
              ))}
            </div>
            <div style={{ fontSize: 13, color: '#888' }}>
              Commission rate: <span style={{ fontWeight: 700, color: 'var(--accent, #d4a843)' }}>{currentTier.commission}</span> |
              {converted < 5 ? ` ${5 - converted} more to Silver` : converted < 15 ? ` ${15 - converted} more to Gold` : converted < 30 ? ` ${30 - converted} more to Platinum` : ' Max tier reached!'}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Recent Activity</div>
            {DEMO_REFERRALS.slice(0, 4).map(r => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border, #2a2a4a)' }}>
                <div>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{r.name}</span>
                  <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>{r.date}</span>
                </div>
                <span style={S.badge(r.status === 'Converted' ? '#4caf50' : r.status === 'Trial' ? '#2196f3' : r.status === 'Signed Up' ? '#ff9800' : '#888')}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Referrals Tab */}
      {tab === 'referrals' && (
        <div style={S.card}>
          <div style={S.cardTitle}>All Referrals</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Name</th>
                  <th style={S.th}>Email</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Referred</th>
                  <th style={S.th}>Plan</th>
                  <th style={S.th}>Commission</th>
                  <th style={S.th}>Paid</th>
                </tr>
              </thead>
              <tbody>
                {DEMO_REFERRALS.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{r.name}</td>
                    <td style={S.td}>{r.email}</td>
                    <td style={S.td}><span style={S.badge(r.status === 'Converted' ? '#4caf50' : r.status === 'Trial' ? '#2196f3' : r.status === 'Signed Up' ? '#ff9800' : '#888')}>{r.status}</span></td>
                    <td style={S.td}>{r.date}</td>
                    <td style={S.td}>{r.plan}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{r.commission}</td>
                    <td style={S.td}>{r.paidOut ? <span style={S.badge('#4caf50')}>Paid</span> : r.commission && r.commission.startsWith('$') ? <span style={S.badge('#ff9800')}>Pending</span> : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Marketing Tools Tab */}
      {tab === 'tools' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Affiliate Marketing Tools</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
              {AFFILIATE_TOOLS.map((t, i) => (
                <div key={i} style={{ padding: 16, borderRadius: 10, border: '1px solid var(--border, #2a2a4a)', textAlign: 'center', cursor: 'pointer' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>{t.icon}</div>
                  <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>{t.desc}</div>
                  <button style={{ ...S.btn(false), fontSize: 11, padding: '6px 14px' }}>Get</button>
                </div>
              ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Custom UTM Builder</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Campaign Source</label>
                <input style={S.input} placeholder="e.g., linkedin, newsletter" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Campaign Medium</label>
                <input style={S.input} placeholder="e.g., social, email, banner" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Campaign Name</label>
                <input style={S.input} placeholder="e.g., spring_referral_2026" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Campaign Content (optional)</label>
                <input style={S.input} placeholder="e.g., sidebar_banner" />
              </div>
            </div>
            <button style={S.btn(true)}>🔗 Generate Tracked Link</button>
          </div>
        </div>
      )}

      {/* Payouts Tab */}
      {tab === 'payouts' && (
        <div>
          <div style={S.card}>
            <div style={S.cardTitle}>Payout Settings</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Payout Method</label>
                <select style={S.input}>
                  <option>PayPal</option>
                  <option>Bank Transfer (ACH)</option>
                  <option>Interac e-Transfer</option>
                  <option>Stripe</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Payout Email / Account</label>
                <input style={S.input} placeholder="your@paypal.com" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Minimum Payout</label>
                <select style={S.input}>
                  <option>$50</option>
                  <option>$100</option>
                  <option>$250</option>
                  <option>$500</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 6 }}>Payout Frequency</label>
                <select style={S.input}>
                  <option>Monthly</option>
                  <option>Bi-weekly</option>
                  <option>Weekly</option>
                </select>
              </div>
            </div>
            <button style={S.btn(true)}>💾 Save Payout Settings</button>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Payout History</div>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Date</th>
                  <th style={S.th}>Amount</th>
                  <th style={S.th}>Method</th>
                  <th style={S.th}>Referrals</th>
                  <th style={S.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { date: 'Feb 1, 2026', amount: '$149.70', method: 'PayPal', referrals: 2, status: 'Completed' },
                  { date: 'Jan 1, 2026', amount: '$99.80', method: 'PayPal', referrals: 1, status: 'Completed' },
                ].map((p, i) => (
                  <tr key={i}>
                    <td style={S.td}>{p.date}</td>
                    <td style={{ ...S.td, fontWeight: 700 }}>{p.amount}</td>
                    <td style={S.td}>{p.method}</td>
                    <td style={S.td}>{p.referrals}</td>
                    <td style={S.td}><span style={S.badge('#4caf50')}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tiers Tab */}
      {tab === 'tiers' && (
        <div style={S.card}>
          <div style={S.cardTitle}>Reward Tiers</div>
          <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>The more you refer, the higher your commission rate and perks.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {TIERS.map((t, i) => (
              <div key={i} style={{ borderRadius: 12, border: `2px solid ${t.name === currentTier.name ? t.color : 'var(--border, #2a2a4a)'}`, padding: 20, position: 'relative' }}>
                {t.name === currentTier.name && (
                  <div style={{ position: 'absolute', top: -10, right: 16, padding: '2px 10px', borderRadius: 10, background: t.color, color: '#000', fontSize: 10, fontWeight: 700 }}>CURRENT</div>
                )}
                <div style={{ fontSize: 28, fontWeight: 800, color: t.color, marginBottom: 4 }}>{t.name}</div>
                <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>{t.range} conversions</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent, #d4a843)', marginBottom: 12 }}>{t.commission}</div>
                <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.6 }}>{t.perks}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ReferralProgramPage;
